import {QueryClient} from "@tanstack/react-query";

/**
 * Creates a pre-configured QueryClient for use across platforms.
 * Platform-specific persistence (e.g. localStorage) is injected externally
 * via the persister pattern — this factory owns only portable defaults.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      /**
       * Mutation defaults registry — global cross-cutting behaviors.
       *
       * Feature-specific behaviors (mutationKey, mutationFn, and optimistic logic)
       * are declared in per-feature mutation option factories (e.g. deleteUserMutation.ts).
       * Cross-cutting concerns (networkMode, retry, retryDelay) live here so they
       * apply uniformly to every mutation in the application.
       *
       * networkMode: 'online' — mutations are paused while offline and automatically
       * resumed on reconnect (TanStack Query v5 built-in resumable mutation behavior).
       */
      mutations: {
        networkMode: "online",
        retry: 3,
        retryDelay: (attempt: number) => Math.min(1_000 * 2 ** attempt, 30_000),
      },
      queries: {
        gcTime: 24 * 60 * 60 * 1000, // 24h — keep unused data in memory for 24 hours
        refetchOnWindowFocus: false,
        retry: 2,
        staleTime: 30_000, // 30s — data is considered fresh for 30 seconds
      },
    },
  });
}
